import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ArticleManualConfig, HydratedArticleItem } from '@news-cms/shared';
import { groupContentsArticlePickerQueryOptions } from '@/lib/group-contents';
import { ArticlePickerDialog } from './article-picker-dialog';
import { ReorderableItemList } from './reorderable-item-list';

interface Props {
  config: ArticleManualConfig;
  maxCount: number;
  onChange: (next: ArticleManualConfig) => void;
  /** Server-hydrated items keyed in admin order — preferred title/thumb
   *  source when present (e.g. inside the /homepage slot editor). Falls
   *  back to the picker-recent-30 cache otherwise. */
  previewItems?: HydratedArticleItem[];
}

/**
 * Renders the admin-picked article list with up/down/remove + an "Add" button
 * that opens the picker dialog. Each row shows a thumbnail + title pulled
 * from either the optional server-hydrated preview (fresh, authoritative)
 * or the picker recent-30 cache (best-effort for create flows).
 *
 * The list shell (count header, empty state, reorder/remove buttons) is
 * shared with the video-manual editor via <ReorderableItemList>.
 */
export function ConfigEditorArticleManual({ config, maxCount, onChange, previewItems }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [titleCache, setTitleCache] = useState<Record<string, HydratedArticleItem>>({});

  // One picker query (recent-30) gives us cover/title for the common case.
  // Edits inserted via the picker dialog ride the same cache via onPick.
  // Dep on `.data` (stable reference) not the query object (new array each
  // render via useQueries) — the latter caused an infinite render loop.
  const lookupQuery = useQuery({
    ...groupContentsArticlePickerQueryOptions(),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!lookupQuery.data) return;
    setTitleCache((prev) => {
      const next = { ...prev };
      for (const a of lookupQuery.data) if (!next[a.id]) next[a.id] = a;
      return next;
    });
  }, [lookupQuery.data]);
  // Merge server-hydrated preview into the cache (authoritative — wins
  // over picker cache).
  useEffect(() => {
    if (!previewItems) return;
    setTitleCache((prev) => {
      const next = { ...prev };
      for (const a of previewItems) next[a.id] = a;
      return next;
    });
  }, [previewItems]);

  const addArticle = (article: HydratedArticleItem) => {
    if (config.items.some((it) => it.postId === article.id)) return;
    if (config.items.length >= maxCount) return;
    setTitleCache((prev) => ({ ...prev, [article.id]: article }));
    onChange({ ...config, items: [...config.items, { postId: article.id }] });
  };

  return (
    <>
      <ReorderableItemList
        items={config.items}
        maxCount={maxCount}
        addLabel="เพิ่มบทความ"
        emptyText='ยังไม่มีบทความ — กดปุ่ม "เพิ่มบทความ" เพื่อเลือก'
        onAddClick={() => setPickerOpen(true)}
        onChange={(items) => onChange({ ...config, items })}
        getKey={(it, idx) => `${it.postId}-${idx}`}
        renderItem={(it) => {
          const article = titleCache[it.postId];
          return (
            <>
              {article?.coverImageUrl ? (
                <img
                  src={article.coverImageUrl}
                  alt=""
                  className="h-10 w-16 shrink-0 rounded object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.visibility = 'hidden';
                  }}
                />
              ) : (
                <div className="h-10 w-16 shrink-0 rounded bg-slate-100 dark:bg-slate-800" />
              )}
              <div className="min-w-0 flex-1">
                <div className="line-clamp-1 text-sm font-medium text-slate-900 dark:text-slate-50">
                  {article?.title ?? <code className="text-xs">{it.postId}</code>}
                </div>
                {article && (
                  <div className="text-[11px] text-slate-400">
                    {article.categoryName ?? 'ไม่ระบุหมวด'}
                  </div>
                )}
              </div>
            </>
          );
        }}
      />

      <ArticlePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        alreadyPicked={config.items.map((it) => it.postId)}
        onPick={addArticle}
      />
    </>
  );
}
